// The page of a file (um/2026-10-30/03 п.8): a single like a record's — the path back to «Файлы», the name, the file itself in place
// (a picture over the width, a PDF in the browser's viewer, a video or audio player, else the icon of its kind), its properties and
// where it is used. The actions are the list's: download and the link (R-C16). Opened as /s/files?file=<key>.

import { useEffect, useState } from 'react';
import { ago } from '../fields/format.ts';
import { navigate } from '../shell/router.ts';
import { useMeta } from '../sync/react.tsx';
import { InfoList, RelatedBlock, Single, SingleActions, SingleTitle, type RelatedItem } from '../record/single.tsx';
import { Icon } from '../ui/icons.tsx';
import { Button, useToast } from '../ui/primitives.tsx';
import { copyFileLink, useFiles, type FileRow } from './files-index.ts';
import { fileSize, KIND_ICON, KIND_LABEL, kindOf } from './kinds.ts';
import s from './files.module.css';

interface Info { name: string; size: number; type: string; viewable: boolean }

export function FileView({ fileKey, back }: { fileKey: string; back: string }) {
  const toast = useToast();
  const meta = useMeta();
  const { rows } = useFiles();
  const row = rows?.find((r) => r.key === fileKey);
  const [info, setInfo] = useState<Info | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setInfo(null); setError(null);
    fetch(`/api/files-info/${fileKey}`).then(async (r) => { if (!r.ok) throw new Error(r.status === 404 ? 'Файл не найден или у вас нет к нему доступа' : 'Не удалось открыть файл'); return (await r.json()) as Info; })
      .then((x) => { if (alive) setInfo(x); }).catch((e: Error) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [fileKey]);

  const name = info?.name ?? row?.name ?? 'Файл';
  const crumbs = [{ label: 'Файлы', href: '/s/files' }, ...(row?.typeName ? [{ label: row.typeName, href: `/s/files?section=${encodeURIComponent(row.typeName)}` }] : []), { label: name }];
  const onBack = () => navigate(back);
  if (error) return <Single crumbs={crumbs} onBack={onBack}><div className={s.empty}>{error}</div></Single>;

  const kind = kindOf(info?.type ?? row?.type ?? '', name);
  const src = `/api/files/${fileKey}`;
  return (
    <Single crumbs={crumbs} onBack={onBack} label="Файл">
      <SingleTitle>{name}</SingleTitle>
      <SingleActions>
        <a className={s.actionLink} href={src} download={name}><Icon name="download" size={14} />Скачать</a>
        <Button size="sm" icon="link" onClick={() => row && void copyFileLink(row, toast)} disabled={!row}>Ссылка</Button>
      </SingleActions>
      <div className={s.preview} data-kind={kind}>
        {!info ? <div className={s.previewWait} aria-hidden />
          : !info.viewable ? <div className={s.previewIcon}><Icon name={KIND_ICON[kind]} size={48} /><span>{KIND_LABEL[kind]} · {fileSize(info.size)}</span><a className={s.actionLink} href={src} download={name}>Скачать, чтобы открыть</a></div>
          : kind === 'image' ? <img src={`${src}?view=1`} alt={name} />
          : kind === 'pdf' ? <iframe src={`${src}?view=1`} title={name} />
          : kind === 'video' ? <video src={`${src}?view=1`} controls preload="metadata" />
          : <audio src={`${src}?view=1`} controls preload="metadata" />}
      </div>
      <InfoList rows={[
        { icon: KIND_ICON[kind], label: 'Тип', value: info ? `${KIND_LABEL[kind]}${info.type ? ` · ${info.type.split(';')[0]}` : ''}` : null },
        { icon: 'number', label: 'Размер', value: info ? fileSize(info.size) || '0 Б' : null },
        { icon: 'date', label: 'Загружен', value: row ? `${ago(row.at)} · ${new Date(row.at).toLocaleString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : null },
        // A file of a card has no sender of its own: the row is shown for a form's answer.
        ...(row?.author ? [{ icon: 'user', label: 'Кто загрузил', value: row.author }] : []),
      ]} />
      <RelatedBlock title="Где используется" items={usedIn(row, (id) => meta.type(id)?.icon)} empty="Файл не привязан к записи" />
    </Single>
  );
}

/** Where the file is used: the record that holds it (with the field), and the form whose answer brought it. */
function usedIn(row: FileRow | undefined, iconOf: (typeId: string) => string | undefined): RelatedItem[] {
  if (!row) return [];
  const out: RelatedItem[] = [];
  if (row.recordId) out.push({ key: 'rec', icon: (row.typeId && iconOf(row.typeId)) || 'relation', title: row.recordTitle || 'без названия', sub: `${row.typeName}${row.source === 'record' ? ` · поле «${row.field}»` : ''}`, href: `/r/${row.recordId}` });
  if (row.source === 'form') out.push({ key: 'form', icon: 'form', title: row.form, sub: `поле «${row.field}»` });
  return out;
}
